<!--
  Checkout（路由 /checkout）

  還原自 index.pretty.js：render L104-643、staticRenderFns L646-673、script L724-1336
  （原始 production bundle，2022-06 build，normalizeComponent(M, d, u)）。
  render function 已轉回 template，regenerator 狀態機已還原為 async/await，
  逐節點／逐敘述對應，未做結構重構。

  結帳頁，也是整個流程中唯一碰金流的元件：
    - 左欄：取餐地點（外送顯示 Google 路線圖／自取顯示店址）、折價券挑選、金額明細
    - 右欄：備餐時間、取餐時段挑選、姓名電話統編、付款方式（LINE Pay／街口支付）
    - 按下結帳 -> 檢查營業狀態 -> POST /online/payment/request -> 導向金流頁

  金額計算集中在 cal_total_price()；送出的 payload 組裝集中在 apply_form()。
  兩者的分支若有任何遺漏都會造成訂單金額錯誤或付款失敗，修改前請先讀
  analysis/payment_contract.txt。

  ── 轉換時刻意保留的已知瑕疵（勿順手「修正」，會改變行為）──
  1. （已修正 2026-08-18）統編欄位的 is_filled 原本綁 name.length；
     同時該欄位已改標示為「訂單備註」，見下方註解。
  2. 折價券可用性「顯示」用 total_price（總額），但 cal_total_price 實際門檻
     用排除 filter_drink 後的 eligible，兩者可能不一致。
  3. time.available 全元件只被賦值 true，故 order_fail / out_of_hours 文案不可達。
  4. mapRef / time / information_other 三個 ref 未被程式引用（僅 information 用於
     apply_form 的捲動定位），保留作為 DOM 結構驗證依據。
  5. setSelProduct、setOrderType 兩個 action 與 order_type watch 為死碼。
  6. apiGetProducts 的 $router.push('/products') 不帶 ?q=，與 trigger_home() 不同。
  7. apiGetStoreStatus 失敗路徑呼叫過 lockScroll() 卻未配對 unlockScroll()，
     依賴 dialog 的 dialog_close / home 事件補償。
  8. （已修正 2026-08-19）create_time_list 原本自己寫死每日最後時段，
     與後端的 BUSINESS_HOURS 分岔 —— 週六可挑到打烊後兩小時、週日公休
     仍可挑時段。現改為讀 /api/storeStatus 提供的營業時間。

  ── 本次轉換唯一刻意的偏離 ──
  linepayImage / jkosImage：原始 bundle 的 render function 直接引用 webpack 模組
  （2bb4 內聯成 data URI、ef6b 為 img/jkos.png），但 template 區塊讀不到模組作用域，
  只能經由實例存取。依 Home.vue:110/125（heroImage）的既有慣例掛進 data() 並排在
  最後，契約 C 的 25 個欄位順序與初始值皆不受影響。編譯後同樣落在 attrs.src，
  渲染出的 src 字串與原始逐字元相同。

  ⚠️ 未解相依：template 的 `| moment('hh:mm A')` 濾鏡在本專案尚未註冊
     （package.json 與 main.js 都沒有 vue-moment，原始 bundle 由 chunk-vendors 的
     Vue.use 提供）。維持原樣以保證行為等價。
     實際影響：Vue 2 的 resolveFilter 解析失敗時回退為 identity
     （vue/dist/vue.runtime.common.dev.js:2716 `resolveAsset(...) || identity`），
     所以頁面不會崩潰，只會把 Date 原樣印出（"Sun Aug 17 2026 11:15:00 GMT+0800"
     而非 "11:15 AM"），dev build 另外印一行 "Failed to resolve filter: moment"。
     金額與 payload 不受影響。上線前補上 vue-moment 即可，修法在 package.json
     與 main.js，不在本檔。
-->

<template>
  <div id="wrapper" class="m-all-0 wrapper_flex">
    <div>
      <!-- 原 _m(0)：靜態導覽列 -->
      <nav class="min-navbar collapse show shadow">
        <div class="nav-container">
          <div class="nav-content d-flex jc-space"><p>微微糖・青埔形象店</p></div>
        </div>
      </nav>

      <!-- 主內容：購物車有東西且店家未關閉。與下方「購物車空了」是兩個獨立
           的 v-if（層級不同），不是 if/else -->
      <div
        v-if="cart_list.length !== 0 && is_close === false"
        class="contect-container contect_end below-min-navb"
      >
        <div class="row">
          <!-- ── 左欄：地點 / 折價券 / 金額明細 ── -->
          <div class="col-lg-6">
            <div class="card">
              <!-- 外送：地址 + 路線圖。用 v-show 保留 DOM，切換取餐方式時
                   地圖不會重新初始化 -->
              <div v-show="order_type === 'ORDER_TYPE_DELIVERY'">
                <div class="header-container">
                  <div class="d-flex jc-space a-center">
                    <div class="title font-24"><p>{{ $t('delivering_to') }}</p></div>
                  </div>

                  <div class="d-flex a-center p-t-16">
                    <i class="font-18 material-icons m-r-8 t-gray-7">location_on</i>
                    <p class="font-16 t-gray-7">{{ delivery.location }}</p>
                  </div>

                  <div class="card-map m-t-16">
                    <GmapMap
                      ref="mapRef"
                      class="card-map"
                      :center="get_center"
                      :zoom="13"
                      :options="options"
                      map-type-id="roadmap"
                    >
                      <DirectionsRenderer
                        travelMode="DRIVING"
                        :origin="delivery.start_location"
                        :destination="delivery.end_location"
                        :routers="routers"
                      />
                    </GmapMap>
                  </div>
                </div>
              </div>

              <!-- 自取：點擊開啟店家的 Google 地圖頁 -->
              <div
                v-show="order_type === 'ORDER_TYPE_PICK_UP'"
                class="header-container hover"
                @click="trigger_event('location')"
              >
                <p class="title font-24">{{ $t('where') }}</p>
                <div class="d-flex a-center p-t-16">
                  <i class="font-18 material-icons m-r-8 t-gray-7">location_on</i>
                  <p class="font-16 t-gray-7">{{ $t('location') }}</p>
                </div>
              </div>
            </div>

            <!-- 外送：折扣說明（外送折扣自動套用，不需挑券） -->
            <div v-if="order_type === 'ORDER_TYPE_DELIVERY'" class="card">
              <div class="header-container p-b-0">
                <div class="title font-24"><p>{{ $t('discount_title') }}</p></div>
              </div>
              <div class="footer-container">
                <p class="t-gray-7 font-16">{{ $t('delivery_discount_notes') }}</p>
              </div>
            </div>

            <!-- 自取：折價券挑選 -->
            <div v-if="show_discount_select" class="card">
              <div class="header-container p-b-0">
                <div class="title font-24"><p>{{ $t('discount_title') }}</p></div>
              </div>

              <div class="list-container p-t-8">
                <label
                  v-for="(item, index) in discount_data"
                  :key="index"
                  :for="item.discountId"
                >
                  <!-- 已使用過的券不顯示 -->
                  <div
                    v-if="!item.isUse"
                    class="card-list"
                    :class="{ 'card-hover': total_price >= item.discountCondition }"
                  >
                    <div class="d-flex a-center jc-space p-l-40 p-r-40">
                      <div class="d-flex a-center">
                        <!-- 已知瑕疵 2：這裡用總額 total_price 判定，但 cal_total_price
                             的實際門檻是排除 filter_drink 後的金額 -->
                        <div v-if="total_price >= item.discountCondition" class="form-check-label">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="item.discountId"
                            :name="item.discountId"
                            :checked="use_discount === item.discountId"
                            @click="selected_discount(item.discountId)"
                          />
                          <!-- 原 _m(1, true) -->
                          <span class="form-check-sign"><span class="check"></span></span>
                        </div>

                        <div>
                          <div class="d-flex p-b-8 a-center">
                            <p class="font-18">優惠代碼: {{ item.discountId }}</p>
                          </div>

                          <div class="t-gray-7 font-16">
                            <p v-if="item.discountCondition > 0" class="d-inline-block">滿{{ item.discountCondition }}元可享</p>
                            <p v-else class="d-inline-block">消費不限金額一律可享</p>

                            <!-- discountContent > 1 視為折抵金額，否則視為折數（0.88 -> 88 折） -->
                            <p v-if="item.discountContent > 1" class="d-inline-block">{{ item.discountContent }}元優惠</p>
                            <p v-else class="d-inline-block">{{ item.discountContent.toString().split('.')[1] }}折優惠</p>

                            <p v-if="item.maxPrice > 0" class="d-inline-block">，折扣金額上限為{{ item.maxPrice }}元</p>
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="total_price < item.discountCondition"
                        class="title font-16 t-align-right"
                        style="width: 40%"
                      >
                        <p>{{ $t('discount_not_reached', { msg: item.discountCondition }) }}</p>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <!-- 金額明細 -->
            <div class="card">
              <div class="header-container">
                <div class="title font-24"><p>{{ $t('order_details') }}</p></div>
              </div>

              <div class="footer-container p-t-0">
                <div class="d-flex a-center jc-space p-b-8">
                  <div class="title font-18"><p>{{ $t('subtotal') }}</p></div>
                  <div class="title font-18"><p>NT${{ total_price }}</p></div>
                </div>

                <div v-if="show_discount" class="d-flex a-center jc-space p-b-8">
                  <div class="title font-18"><p>{{ $t('discount') }}</p></div>
                  <div class="title font-18"><p>-NT${{ discount }}</p></div>
                </div>

                <div class="d-flex a-center jc-space">
                  <div class="title font-24"><p>{{ $t('total') }}</p></div>
                  <div class="title font-24"><p>NT${{ total_price - discount }}</p></div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── 右欄：備餐時間 / 取餐時段 / 顧客資料 / 付款 ── -->
          <div class="col-lg-6">
            <div class="card">
              <div class="header-container">
                <div class="d-flex jc-space">
                  <div v-if="order_type === 'ORDER_TYPE_DELIVERY'" class="title font-24"><p>{{ $t('prepare_time_2') }}</p></div>
                  <div v-else class="title font-24"><p>{{ $t('prepare_time') }}</p></div>
                  <div class="title font-24"><p>{{ preparation_time }} {{ $t('minutes') }}</p></div>
                </div>
              </div>

              <div class="footer-container p-t-0">
                <p class="t-gray-7 font-16">{{ $t('order_notes_2') }}</p>
                <p class="t-gray-7 font-16 m-t-12">{{ $t('order_notes_3') }}</p>
              </div>
            </div>

            <!-- ref="time" 目前無人引用，保留作為 DOM 結構驗證依據 -->
            <div ref="time" class="card">
              <div
                v-if="access_select_time"
                class="header-sticky-container min-navbar"
                :class="pick_time_actived"
                @click="trigger_pick_time"
              >
                <!-- 已知瑕疵 3：time.available 永不為 false，下面兩個 v-else
                     （order_fail / out_of_hours）在本元件內都是不可達分支。
                     註解不要插在 v-if 與 v-else 之間，否則 v-else 配對會依賴
                     編譯器的 comments 設定 -->
                <div v-if="time.available === true" class="d-flex jc-space">
                  <div v-if="order_type === 'ORDER_TYPE_DELIVERY'" class="title font-24"><p>{{ $t('estimated_delivery') }}</p></div>
                  <div v-else class="title font-24"><p>{{ $t('when') }}</p></div>
                  <div v-if="time.pick_time !== null" class="title font-24"><p>{{ time.pick_time | moment('hh:mm A') }}</p></div>
                </div>
                <div v-else class="d-flex jc-space">
                  <div class="title font-24"><p>{{ $t('order_fail') }}</p></div>
                </div>

                <div v-if="time.available === true">
                  <div class="d-flex a-center p-t-16">
                    <i class="font-18 material-icons m-r-8 t-gray-7">more_time</i>
                    <p class="font-16 t-gray-7">{{ $t('modify') }}</p>
                  </div>
                </div>
                <div v-else>
                  <div class="d-flex a-center p-t-16">
                    <i class="font-18 material-icons m-r-8 t-gray-7">report_problem</i>
                    <p class="font-16 t-gray-7">{{ $t('out_of_hours') }}</p>
                  </div>
                </div>
              </div>

              <div v-if="picker_show === true" class="list-container">
                <label
                  v-for="(t, index) in time_list"
                  :key="index"
                  class="time-picker-label"
                  :class="{ actived: t === time.pick_time }"
                  @click="select_time(t)"
                >
                  <div class="card-list card-hover">
                    <p class="t-align-center">{{ t | moment('hh:mm A') }}</p>
                  </div>
                </label>
              </div>
            </div>

            <!-- 顧客資料（必填） -->
            <div v-if="access_checkout" class="card">
              <div
                ref="information"
                class="header-container p-b-0"
                :class="{ alert: name_error || number_error }"
              >
                <div class="d-flex jc-space">
                  <div class="title font-24"><p>{{ $t('information') }}</p></div>
                  <div class="required-label font-16"><p>{{ $t('require') }}</p></div>
                </div>
              </div>

              <div class="footer-container">
                <div class="mui-textfield" :class="{ is_filled: name.length > 0 }">
                  <input v-model="name" :class="{ error: name_error }" @blur="check_name" />
                  <label tabindex="-1">{{ $t('name') }}</label>
                </div>

                <div class="mui-textfield" :class="{ is_filled: number.length > 0 }">
                  <input v-model="number" type="tel" :class="{ error: number_error }" @blur="check_number" />
                  <label tabindex="-1">{{ $t('number') }}</label>
                </div>
              </div>
            </div>

            <!-- 統一編號（選填）。與上一張卡片是兩個獨立的 v-if，不是同一個 -->
            <div v-if="access_checkout" class="card">
              <div
                ref="information_other"
                class="header-container p-b-0"
                :class="{ alert: name_error || number_error }"
              >
                <div class="d-flex jc-space">
                  <div class="title font-24"><p>{{ $t('other_information') }}</p></div>
                  <div class="optional-label font-16"><p>{{ $t('optional') }}</p></div>
                </div>
              </div>

              <div class="footer-container">
                <!--
                  這個欄位的鍵名是 unified_compilation（原本標示為「統編」），
                  但店家免用統一發票，後端也從未讀取這個值 —— 它只是原封不動
                  存進 order_series_content 的 JSON。

                  實際資料（623 筆訂單）：25 筆填了 8 位數字的統編、
                  19 筆填的是外送指示（「放管理室外送桌上」「使用環保杯 二個」），
                  其餘留空。也就是說標示為統編反而誤導客戶填了拿不到發票的號碼，
                  而真正在用的是訂單層級的備註。

                  因此改標示為「訂單備註」。鍵名維持 unified_compilation 不動 ——
                  改鍵名會同時破壞後端、接單終端與既有 623 筆訂單的 JSON 結構，
                  而標籤才是客戶看到的東西。

                  同時修正 is_filled 綁在 name.length 的瑕疵（原本輸入姓名會讓
                  這一格的浮動標籤跟著上移，而在本欄輸入卻不會）。
                -->
                <div class="mui-textfield" :class="{ is_filled: unified_compilation.length > 0 }">
                  <input v-model="unified_compilation" />
                  <label tabindex="-1">{{ $t('unified_compilation') }}</label>
                </div>
              </div>
            </div>

            <!-- 付款方式 -->
            <div class="card">
              <div class="header-container p-b-0" :class="{ alert: name_error || number_error }">
                <div class="title font-24"><p>{{ $t('payment') }}</p></div>
              </div>

              <div class="footer-container">
                <span class="t-red-12 font-16">{{ $t('agree_1') }}</span>

                <div class="m-b-12 m-t-12">
                  <!--
                    這兩段 <label> 只差在 radio 的 value 與圖檔，看起來很適合用 v-for
                    收斂 —— 但不要這麼做。v-for 必須把 value 改成動態綁定（:value），
                    Vue 會因此把它從 attrs 搬到 domProps，渲染出的 DOM 就不再有
                    value="line" 這個屬性（只剩 el.value），外層還會多出 key。
                    這是金流欄位，維持兩段展開的靜態寫法才與原始 render 完全一致。
                  -->
                  <label>
                    <div class="card-list card-hover">
                      <div class="d-flex a-center">
                        <div class="form-check-label">
                          <input v-model="payment" class="form-check-input" type="radio" value="line" />
                          <!-- 原 _m(2) -->
                          <span class="circle"><span class="check"></span></span>
                        </div>
                        <img class="pay-icon" :src="linepayImage" />
                      </div>
                    </div>
                  </label>

                  <label>
                    <div class="card-list card-hover">
                      <div class="d-flex a-center">
                        <div class="form-check-label">
                          <input v-model="payment" class="form-check-input" type="radio" value="jkos" />
                          <!-- 原 _m(3) -->
                          <span class="circle"><span class="check"></span></span>
                        </div>
                        <img class="pay-icon" :src="jkosImage" />
                      </div>
                    </div>
                  </label>
                </div>

                <button v-if="access_checkout" class="btn btn-primary btn-fill" @click="apply_form">
                  <p class="t-gray-10">{{ $t('checkout') }}</p>
                </button>
                <button v-else class="btn btn-grey btn-fill" disabled>
                  <p class="t-gray-10 font-18">{{ $t('order_fail') }}</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 購物車空了。注意層級：這是上面那個 <div> 的兄弟節點，不是它的子節點 -->
    <div v-if="cart_list.length === 0" class="d-flex fd-column-full jc-center a-center below-min-navb">
      <i class="material-icons icon-142 p-30 t-gray-12">shopping_cart</i>
      <p>{{ $t('cart_empty') }}</p>
      <button class="btn btn-primary btn-round m-t-16" @click="trigger_home">
        <p>{{ $t('browse') }}</p>
      </button>
    </div>

    <v-footer />

    <v-loading v-if="loading_show" />

    <v-dialog v-if="dialog_data.show" :dialog="dialog_data" @event="trigger_event" />
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapGetters } from 'vuex'

import DirectionsRenderer from '@/components/DirectionsRenderer.js'
import VDialog from '@/components/VDialog.vue'
import VFooter from '@/components/VFooter.vue'
import VLoading from '@/components/VLoading.vue'
import * as types from '@/store/mutation-types'
import { getStorage, lockScroll, unlockScroll, sleep } from '@/utils'
import linepayImage from '@/assets/linepay.png'
import jkosImage from '@/assets/jkos.png'

/** 店家座標（青埔形象店），delivery.start_location 與 center 的初始值 */
const STORE_LOCATION = { lat: 25.0142841153413, lng: 121.21120369949432 }

/** trigger_event('location') 開啟的店家 Google 地圖頁 */
const STORE_MAP_URL =
  'https://www.google.com/maps/place/%E5%BE%AE%E5%BE%AE%E7%B3%96%E3%83%BB%E9%9D%92%E5%9F%94%E5%BD%A2%E8%B1%A1%E5%BA%97/@25.0142829,121.2112037,15z/data=!4m5!3m4!1s0x0:0xcae7204842ede5a!8m2!3d25.0142877!4d121.2112085'

/** 由外送頁寫入的 localStorage 鍵（有效期 30 天），本頁只讀不寫 */
const KEY_ROUTES = '5ew41gf56ew456-routes'
const KEY_ROUTES_RESPONSE = '5ew41gf56ew456-routes-response'

/** 提袋商品的 id 哨兵值：不計入杯數，改計入 total_bags */
const BAG_DRINK_ID = 'drink00'

/** 外送可結帳的距離上限（公里） */
const MAX_DELIVERY_DISTANCE_KM = 10

/** 外送折扣：每滿 11 件（含提袋）折 NT$50 */
const DELIVERY_DISCOUNT_AMOUNT = 50
const DELIVERY_DISCOUNT_UNIT = 11

/** 備餐時間：基底 10 分鐘，每滿 6 杯（不含提袋）再加 10 分鐘 */
const CUPS_PER_TIME_STEP = 6
const MINUTES_PER_STEP = 10

/** 外送時間倍率：Math.floor(1.5 * delivery.time) 分鐘 */
const DELIVERY_TIME_FACTOR = 1.5

/** 取餐時段間隔（分鐘），同時也是對齊到下一個整點刻度的單位 */
const SLOT_INTERVAL_MINUTES = 15

/** 60 分鐘 = 1 小時，用於把 Date 換算成「當日第幾分鐘」 */
const MINUTES_PER_HOUR = 60

/**
 * 'HHMM' -> 當日第幾分鐘；非法或 null 回 null。
 *
 * 營業時間改由 /api/storeStatus 提供（後端的 BUSINESS_HOURS 是唯一來源），
 * 前端不再自己寫死一份。原本寫死的四個 LAST_SLOT_* 與後端已經分岔：
 *
 *     週六　後端 10:00–17:45　前端可選到 19:45　（客人能挑到打烊後兩小時）
 *     週日　後端公休　　　　　前端可選到 19:45
 *     文案　「訂餐時間為 11:00 到 21:45」　與兩者都不符
 */
function slot_minutes(hhmm) {
  if (typeof hhmm !== 'string' || !/^\d{4}$/.test(hhmm)) return null

  const h = Number(hhmm.slice(0, 2))
  const m = Number(hhmm.slice(2))

  if (h > 23 || m > 59) return null

  return MINUTES_PER_HOUR * h + m
}

/** 'HHMM' -> 'HH:MM'，給對話框文案用 */
function slot_label(hhmm) {
  return hhmm.slice(0, 2) + ':' + hhmm.slice(2)
}

/** apply_form 捲動定位時扣掉的 sticky navbar 高度（px） */
const NAVBAR_OFFSET_PX = 60

/** 停留超過 5 分鐘就要求重新選取餐時間，避免送出過期的 pick_up_time */
const MAX_STAY_MS = 300000

/** 確認營業狀態後、重畫路線圖前的等待時間（ms） */
const MAP_SETTLE_MS = 1000

/** 後端回應碼：0 成功、1 售完（僅 apiPostCart 有此分支），其餘為一般錯誤 */
const CODE_OK = 0
const CODE_SOLD_OUT = 1

/** drink_sell / feed_sell 的售完判定是字串 "false"，不是布林 false */
const SELL_SOLD_OUT = 'false'

export default {
  components: {
    DirectionsRenderer,
    'v-loading': VLoading,
    'v-dialog': VDialog,
    'v-footer': VFooter
  },

  data() {
    return {
      client: this.getClientID(),
      cart_list: this.getCartList(),
      is_close: this.getClose(),
      loading_show: false,
      picker_show: false,
      time_list: [],
      name: this.getUserName(),
      number: this.getUserNumber(),
      unified_compilation: this.getRemark(),
      name_error: false,
      number_error: false,
      discount_data: null,
      discount: 0,
      scroll_y: 0,
      use_discount: this.getDiscount(),
      total_price: 0,
      preparation_time: 0,

      time: { current_time: null, pick_time: null, available: true },

      dialog_data: { show: false, title: null, content: null, event: 'close' },

      order_type: this.getOrderType(),

      delivery: {
        location: this.getLocation(),
        start_location: { ...STORE_LOCATION },
        end_location: null,
        center: { ...STORE_LOCATION },
        distance: 0,
        fee: 0,
        time: 0,
        place: null
      },

      options: {
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        rotateControl: false,
        disableDefaultUi: true,
        clickableIcons: false
      },

      routers: null,
      access_checkout: false,
      payment: 'line',

      // 契約 C 的 25 個欄位到 payment 為止，順序與初始值與原始碼逐一對應。
      // 以下兩個是本次轉換唯一新增的欄位：原始 render function 直接引用 webpack
      // 模組，但 template 區塊讀不到模組作用域，只能經實例存取。附加在尾端，
      // 不影響上面 25 個欄位的順序。慣例同 Home.vue:125 的 heroImage。
      linepayImage,
      jkosImage
    }
  },

  computed: {
    /** 回傳的是「字串」不是物件：hover / show 兩個 class 的組合 */
    pick_time_actived() {
      let s = ''
      if (this.time.available) s += 'hover'
      if (this.picker_show === true) s += ' show'
      return s
    },

    get_center() {
      return this.delivery.center
    },

    /** 能不能挑取餐時段。與 get_access_checkout 的差別只在少了 time.available 前置條件 */
    access_select_time() {
      return this.order_type === types.ORDER_TYPE_DELIVERY
        ? this.total_price - this.discount >= this.delivery.fee &&
            this.delivery.distance <= MAX_DELIVERY_DISTANCE_KM
        : this.total_price > 0
    },

    show_discount() {
      return this.order_type === types.ORDER_TYPE_DELIVERY || this.use_discount !== null
    },

    show_discount_select() {
      return this.order_type !== types.ORDER_TYPE_DELIVERY && this.discount_data !== null
    }
  },

  watch: {
    // deep 對 Number 沒有實效，保留原樣
    preparation_time: {
      deep: true,
      handler() {
        this.create_time_list()
      }
    },

    use_discount() {
      this.cal_total_price()
    },

    // 本元件內從不改 order_type，等同死碼，保留原樣
    order_type() {
      this.cal_total_price()
    },

    total_price() {
      this.access_checkout = this.get_access_checkout()
    }
  },

  methods: {
    ...mapActions([
      'setSelProduct',
      'setUserName',
      'setUserNumber',
      'setRemark',
      'setClientID',
      'setOrderID',
      'removeItemToCart',
      'setDiscount',
      'setOrderType',
      'editItemToCart',
      'setOpenCart',
      'setStoreStatus'
    ]),
    ...mapGetters([
      'getCartList',
      'getClose',
      'getUserName',
      'getUserNumber',
      'getRemark',
      'getClientID',
      'getSetupTime',
      'getDiscount',
      'getLocation',
      'getOrderType',
      'getBusinessHours'
    ]),

    get_access_checkout() {
      return (
        !!this.time.available &&
        (this.order_type === types.ORDER_TYPE_DELIVERY
          ? this.total_price - this.discount >= this.delivery.fee &&
            this.delivery.distance <= MAX_DELIVERY_DISTANCE_KM
          : this.total_price > 0)
      )
    },

    /** 找出目前選用的券在 discount_data 中的索引。呼叫端須確保 discount_data !== null */
    /*
     * 找出目前選用的那張券在 discount_data 裡的位置。
     *
     * ⚠️ 必須同時排除已使用的券（isUse）。
     *
     * use_discount 是從 localStorage 還原的（data 的 this.getDiscount()），
     * 而 discount_data 是每次進頁面重新向後端要的、**包含已使用的券**
     * （getUserDiscount 一律回傳並附上 isUse 旗標）。
     *
     * 2026-08-25 之前這裡只比 discountId：客人選了券、去付款、券被核銷，
     * 之後回到結帳頁時 use_discount 被還原、這裡仍然找得到那張券，
     * 於是前端照樣套用折扣 —— 但後端的 checkOrderUserDiscount 要求
     * is_use == 0，必定退件。
     *
     * 而客人解不掉：模板用 v-if="!item.isUse" 把已使用的券藏起來
     * （見上方的券清單），所以畫面上根本沒有那張券可以點掉。
     * 結果就是「一直結不了帳，而且看不出為什麼」。
     *
     * 加上 isUse 的排除之後，findIndex 回 -1，cal_total_price 既有的
     * `idx === -1` 分支就會把 use_discount 清成 null —— 自我修復，
     * 不需要額外的處理。
     */
    search_use_discount() {
      return this.discount_data.findIndex(
        (d) => d.discountId === this.use_discount && !d.isUse
      )
    },

    /**
     * 核心計價。結束後會更新 total_price、discount、preparation_time 三個欄位。
     *
     * sum            總額（含提袋）
     * drink_count    杯數，不含提袋，決定備餐時間
     * delivery_count 件數，含提袋，決定外送折扣
     * eligible       可折抵金額 —— filter_drink 是「排除」清單，不在清單內的才累加
     */
    cal_total_price() {
      let sum = 0
      let drink_count = 0
      let delivery_count = 0
      let eligible = 0

      // 這裡原始碼是真的 for...of（babel 的 for-of iterator helper 樣板），
      // 與 apply_form / apiGetProducts 的 for...in 不同，兩者不要互相「統一」
      for (const item of this.cart_list) {
        sum += item.total_price
        if (item.drink_id !== BAG_DRINK_ID) drink_count += item.count

        if (this.order_type === types.ORDER_TYPE_DELIVERY) {
          delivery_count += item.count
        } else if (this.use_discount !== null) {
          const idx = this.search_use_discount()
          if (
            idx === -1 ||
            this.discount_data[idx].filter_drink.indexOf(item.drink_id) === -1
          ) {
            eligible += item.total_price
          }
        }
      }

      this.total_price = sum

      if (this.order_type === types.ORDER_TYPE_DELIVERY) {
        // 外送：折扣直接由件數算出，不寫回 store（不呼叫 setDiscount）
        eligible +=
          DELIVERY_DISCOUNT_AMOUNT * Math.floor(delivery_count / DELIVERY_DISCOUNT_UNIT)
        this.discount = eligible
      } else {
        if (this.discount_data === null) {
          this.discount = 0
        } else if (this.use_discount !== null) {
          const idx = this.search_use_discount()

          if (idx !== -1) {
            const coupon = this.discount_data[idx]
            let amount = 0

            if (eligible >= coupon.discountCondition) {
              // discountContent > 1 視為折抵金額，否則視為折數（0.88 -> 折掉 12%）
              amount =
                coupon.discountContent > 1
                  ? coupon.discountContent
                  : eligible - Math.round(eligible * coupon.discountContent)
            } else {
              // 未達門檻就退掉這張券。會觸發 use_discount watch 再算一次
              this.use_discount = null
            }

            if (coupon.maxPrice > 0) {
              this.discount = amount > coupon.maxPrice ? coupon.maxPrice : amount
            } else {
              this.discount = amount
            }
          } else {
            this.use_discount = null
            this.discount = 0
          }
        } else {
          this.discount = 0
        }

        this.setDiscount(this.use_discount)
      }

      this.preparation_time =
        MINUTES_PER_STEP * (Math.floor(drink_count / CUPS_PER_TIME_STEP) + 1) +
        this.getSetupTime()

      if (this.order_type === types.ORDER_TYPE_DELIVERY) {
        this.preparation_time += Math.floor(DELIVERY_TIME_FACTOR * this.delivery.time)
      }
    },

    /**
     * 產生可選的取餐時段，由 preparation_time watch 觸發。
     *
     * 最早可取時間 = 現在 + 備餐時間；時段清單則從該時間「往後對齊到下一個
     * 15 分刻度」開始，每 15 分鐘一個，直到當日的 cutoff 或跨日為止。
     * 注意 time.pick_time 存的是「未對齊」的最早可取時間，本身不在 time_list 內。
     */
    create_time_list() {
      const cursor = new Date()
      this.time.current_time = new Date() // 刻意另建一個實例，與 cursor 不共用
      const day = cursor.getDate()

      cursor.setMinutes(cursor.getMinutes() + this.preparation_time)

      const earliest = new Date(cursor.getTime())

      /*
       * 營業時間來自 /api/storeStatus。三種情況都產生空清單：
       *   ・尚未取得（進頁面時 API 還沒回來）—— 回來後 apiGetStoreStatus 會再叫一次
       *   ・今日公休（後端回 null）
       *   ・格式非預期
       */
      const hours = this.getBusinessHours() || {}
      const open_at = slot_minutes(hours.open)
      const close_at = slot_minutes(hours.close)

      const pad =
        SLOT_INTERVAL_MINUTES * (Math.floor(cursor.getMinutes() / SLOT_INTERVAL_MINUTES) + 1) -
        cursor.getMinutes()
      cursor.setMinutes(cursor.getMinutes() + pad)

      this.time.pick_time = earliest
      this.time.available = true
      this.time_list = []

      if (open_at === null || close_at === null) return

      // 跨日時 day !== cursor.getDate()，迴圈不進入，time_list 保持空陣列
      while (
        MINUTES_PER_HOUR * cursor.getHours() + cursor.getMinutes() <= close_at &&
        day === cursor.getDate()
      ) {
        /*
         * 開店前的時段一併排除。營業中下單時 cursor 一定已經晚於開店時間
         * （它從「現在 + 製作時間」起算），所以這個條件平常不會生效；
         * 但測試模式的 bypassBusinessHours 會讓非營業時段也能下單，
         * 那時就靠它擋掉「早上七點挑七點十五取餐」這種時段。
         */
        if (MINUTES_PER_HOUR * cursor.getHours() + cursor.getMinutes() >= open_at) {
          this.time_list.push(new Date(cursor.getTime()))
        }

        cursor.setMinutes(cursor.getMinutes() + SLOT_INTERVAL_MINUTES)
      }
    },

    trigger_pick_time() {
      if (!this.time.available) return

      this.picker_show = !this.picker_show

      if (this.picker_show) {
        this.scroll_y = window.scrollY
      } else {
        window.scrollTo({ top: this.scroll_y, left: 0 })
      }
    },

    trigger_event(event) {
      switch (event) {
        case 'dialog_close':
          unlockScroll()
          this.dialog_data.show = false
          break

        case 'location':
          window.open(STORE_MAP_URL, '_blank')
          break

        /*
         * 外送路線資料缺漏時的復原：清掉可能已失效的快取，回首頁重選地址。
         * 不清的話客人回到首頁仍會讀到同一份壞掉的狀態，等於原地打轉。
         */
        case 'delivery_reset':
          unlockScroll()
          this.dialog_data.show = false
          localStorage.removeItem(KEY_ROUTES)
          localStorage.removeItem(KEY_ROUTES_RESPONSE)
          this.$router.push('/' + (this.client !== null ? '?q=' + this.client : ''))
          break

        case 'home':
          this.loading_show = false
          unlockScroll()
          this.trigger_home()
          break

        case 'get_product':
          // 已知瑕疵 8：這裡不呼叫 unlockScroll()
          this.loading_show = true
          this.dialog_data.show = false
          this.apiGetProducts()
          break

        case 'refresh':
          // 已知瑕疵 8：這裡不呼叫 unlockScroll()
          this.create_time_list()
          window.scrollTo(0, 0)
          this.dialog_data.show = false
          break
      }
    },

    trigger_home() {
      let path = '/products'
      if (this.client !== null) path += '?q=' + this.client
      this.$router.push(path)
    },

    select_time(t) {
      this.time.pick_time = t
      this.picker_show = !this.picker_show
      window.scrollTo({ top: this.scroll_y, left: 0 })
    },

    check_name() {
      this.name_error = this.name.length === 0
    },

    check_number() {
      this.number_error = this.number.length === 0
    },

    /** 再點一次同一張券就取消選用。會觸發 use_discount watch 重算金額 */
    selected_discount(id) {
      if (id === this.use_discount) {
        this.use_discount = null
      } else {
        this.use_discount = id
      }
    },

    update_map() {
      this.routers = getStorage(KEY_ROUTES_RESPONSE)
    },

    /** 逐欄位賦值而非整包取代，與原始碼一致 */
    show_dialog(title, content, event) {
      this.loading_show = false
      this.dialog_data.show = true
      this.dialog_data.title = title
      this.dialog_data.content = content
      this.dialog_data.event = event
    },

    api_error() {
      this.show_dialog(this.$t('err'), this.$t('err_9000'), 'dialog_close')
    },

    /**
     * 重抓菜單，把購物車裡售完的品項清掉、加料失效的品項改回原價，
     * 然後帶著購物車回菜單頁讓使用者重選。
     * 由 dialog 的 get_product 事件觸發（結帳時後端回報售完）。
     */
    async apiGetProducts() {
      try {
        const res = await axios.get('/api/drinkMenu')

        if (res.data.code_status !== CODE_OK) {
          this.show_dialog(this.$t('err'), res.data.response_data.message, 'dialog_close')
          return
        }

        const sold_out = []
        const menu = res.data.response_data.menu

        // 原始碼是三層 for...in（regenerator 的 keys()），索引為字串，保留原樣。
        // ⚠️ 不要改成 for...of：for...in 列舉的是鍵（含繼承的可列舉屬性、跳過
        //    稀疏洞），與 for...of 在非稠密陣列上的行為不同。
        for (const z in this.cart_list) {
          for (const t in menu) {
            for (const b in menu[t].drink_items) {
              const drink = menu[t].drink_items[b]
              if (drink.drink_id !== this.cart_list[z].drink_id) continue

              // 飲品本身售完：記下來稍後移除，且不呼叫 editItemToCart
              if (drink.drink_sell === SELL_SOLD_OUT) {
                /*
                 * ⚠️ 這裡是本檔唯一刻意偏離原始 bundle 的修正（bug fix）。
                 *
                 * 原始碼 push 的是 drink_id（例如 "drink0102"），但 store 的
                 * CART_DELETE mutation 比對的是購物車項目的 uuid：
                 *     findIndex(item => item.id === id)
                 * 兩者永遠不相等 -> findIndex 回傳 -1 -> splice(-1, 1)
                 * -> 刪掉購物車的「最後一筆」，而售完的那杯留著。
                 *
                 * 使用者實際會遇到的：結帳時後端回報售完、按下確認後，售完的
                 * 品項沒被移除，反而是另一筆無關的飲料消失。再按一次結帳又刪一筆，
                 * 購物車內容被逐步吃掉，而畫面上只看得到「東西不見了」。
                 *
                 * 改為 push 購物車項目的 id（uuid），與 mutation 的比對對齊。
                 */
                sold_out.push(this.cart_list[z].id)
                continue
              }

              this.cart_list[z].feeds = drink.feeds

              if (this.cart_list[z].feed_selected !== null) {
                for (const feed of this.cart_list[z].feeds) {
                  if (
                    this.cart_list[z].feed_selected.item === feed.item &&
                    feed.feed_sell === SELL_SOLD_OUT
                  ) {
                    // 加料售完：退回無加料，價格改回飲品原價 * 數量
                    this.cart_list[z].feed_selected = null
                    this.cart_list[z].feed_sold_out = feed.item
                    this.cart_list[z].total_price =
                      this.cart_list[z].drink_price * this.cart_list[z].count
                    break
                  }
                }
              }

              // feed_selected 為 null 或加料迴圈跑完，兩條路徑都會走到這裡
              this.editItemToCart(this.cart_list[z])
            }
          }
        }

        for (const x in sold_out) this.removeItemToCart(sold_out[x])

        this.cart_list = this.getCartList()
        this.loading_show = false
        unlockScroll()
        this.setOpenCart(true)
        // 已知瑕疵 6：這裡不帶 ?q=，與 trigger_home() 不同
        this.$router.push('/products')
      } catch (e) {
        console.log(e)
        this.api_error()
      }
    },

    /**
     * 確認店家營業中。
     * payload 只作分流用、不會送出：帶 payload 代表是結帳流程，接著送單；
     * 帶 null 代表是進頁面時的檢查，接著重畫外送路線圖。
     */
    async apiGetStoreStatus(payload) {
      // 這兩行在 try 之外，與原始碼一致
      this.loading_show = true
      lockScroll()

      try {
        const res = await axios.get('/api/storeStatus')

        if (res.data.code_status !== CODE_OK) {
          // 已知瑕疵 7：這條路徑沒有配對的 unlockScroll()，靠 dialog 事件補償
          this.show_dialog(this.$t('err'), res.data.response_data.message, 'dialog_close')
          return
        }

        /*
         * 存進 vuex。原本 Checkout 拿到回應只讀 storeIsOpen 就丟掉，
         * 營業時間只有 Products.vue 會寫入 —— 但客人可能直接落在 /checkout
         * （LINE Pay 取消付款就會導回這裡），那條路徑不經過 Products，
         * 沒有營業時間，取餐時段清單就會是空的。
         */
        this.setStoreStatus(res.data.response_data)

        if (!res.data.response_data.storeIsOpen) {
          const hours = this.getBusinessHours() || {}
          const message =
            hours.open && hours.close
              ? this.$t('store_close', {
                  open: slot_label(hours.open),
                  close: slot_label(hours.close)
                })
              : this.$t('store_close_holiday')

          this.show_dialog(this.$t('close'), message, 'home')
          return
        }

        // 營業時間可能是這次才取得的，重算一次取餐時段
        this.create_time_list()

        if (payload !== null) {
          this.apiPostCart(payload) // 刻意不 await，與原始碼一致
          return
        }

        this.is_close = !res.data.response_data.storeIsOpen
        this.loading_show = false
        unlockScroll()

        // 等地圖元件掛好再塞路線資料
        await sleep(MAP_SETTLE_MS)
        if (this.order_type === types.ORDER_TYPE_DELIVERY) this.update_map()
      } catch (e) {
        this.api_error()
      }
    },

    /** bfcache 還原時解除遮罩與捲動鎖定，見 mounted 的說明 */
    on_page_show(event) {
      if (!event.persisted) {
        return
      }

      this.loading_show = false
      unlockScroll()
    },

    /** 送單並導向金流頁。成功時刻意不關閉 loading_show，讓遮罩撐到頁面跳走 */
    async apiPostCart(payload) {
      try {
        const res = await axios.post('/online/payment/request', payload)

        if (res.data.code_status === CODE_OK) {
          this.setOrderID(res.data.response_data.id)
          window.location.href = res.data.response_data.url
        } else if (res.data.code_status === CODE_SOLD_OUT) {
          this.show_dialog(this.$t('sold_out'), res.data.response_data.message, 'get_product')
        } else {
          this.show_dialog(this.$t('err'), res.data.response_data.message, 'dialog_close')
        }
      } catch (e) {
        this.api_error()
      }
    },

    /** 取得這位 LINE 使用者的折價券。判斷用 data 快照 client，URL 用即時的 getClientID() */
    async apiGetUserDiscount() {
      if (this.client === null) {
        this.use_discount = null
        this.cal_total_price()
        return
      }

      try {
        const res = await axios.get('/api/userDiscount?q=' + this.getClientID())

        if (res.data.code_status === CODE_OK) {
          if (res.data.response_data.discount !== undefined) {
            this.discount_data = res.data.response_data.discount
          }
          this.cal_total_price()
        } else {
          this.use_discount = null
          this.cal_total_price()
          this.show_dialog(this.$t('err'), res.data.response_data.message, 'dialog_close')
        }
      } catch (e) {
        this.use_discount = null
        this.cal_total_price()
        console.log(e)
        this.api_error()
      }
    },

    /**
     * 結帳主流程：檢查停留時間 -> 檢查必填 -> 組 payload -> 確認營業 -> 送單。
     *
     * pick_up_time 會加上使用者在本頁停留的毫秒數，讓送出的取餐時間跟著往後推。
     * 若 time.current_time 為 null（create_time_list 從未執行，例如空購物車）
     * 第一行就會拋 TypeError —— 原始碼即如此。
     */
    apply_form() {
      const elapsed = new Date().getTime() - this.time.current_time.getTime()

      // 刻意寫成 !(elapsed < MAX_STAY_MS) 而非 elapsed >= MAX_STAY_MS：
      // 原始碼的判斷是 `if (elapsed < 3e5) {...} else {...}`，兩者在 elapsed
      // 為 NaN 時的走向不同，此處保留原始極性
      if (!(elapsed < MAX_STAY_MS)) {
        this.show_dialog(this.$t('stay_too_long'), this.$t('update_pick_up_time'), 'refresh')
        return
      }

      this.check_name()
      this.check_number()

      if (this.name_error || this.number_error) {
        const top = this.$refs.information.getBoundingClientRect().top
        window.scrollTo({
          top: window.scrollY + top - NAVBAR_OFFSET_PX,
          left: 0,
          behavior: 'smooth'
        })
        return
      }

      const payload = {
        drink_list: [],
        pick_up_time: new Date(this.time.pick_time.getTime() + elapsed).toISOString(),
        total_price: 0,
        total_drink: 0,
        total_bags: 0,
        name: this.name,
        number: this.number,
        unified_compilation: this.unified_compilation,
        discountId: null,
        discount: 0,
        client_id: this.getClientID(),
        map: null,
        payment: this.payment
      }

      // 原始碼是 for...in（索引為字串）。⚠️ 不要改成 for...of —— 這個迴圈同時
      // 累加 payload.total_price / total_drink / total_bags，也就是實際請款金額，
      // 兩種迴圈在稀疏或被掛過額外屬性的陣列上結果不同。
      for (const i in this.cart_list) {
        const line = {
          drink: this.cart_list[i].drink,
          drink_id: this.cart_list[i].drink_id,
          drink_price: this.cart_list[i].drink_price,
          feed_selected:
            this.cart_list[i].feed_selected === null
              ? null
              : this.cart_list[i].feed_selected.item,
          ice_selected:
            this.cart_list[i].ice_selected === null ? null : this.cart_list[i].ice_selected,
          suger_selected:
            this.cart_list[i].suger_selected === null ? null : this.cart_list[i].suger_selected,
          count: this.cart_list[i].count,
          total_price: this.cart_list[i].total_price,
          message: this.cart_list[i].message
        }

        if (line.drink_id !== BAG_DRINK_ID) {
          payload.total_drink += line.count
        } else {
          payload.total_bags += line.count
        }

        payload.total_price += line.total_price
        payload.drink_list.push(line)
      }

      if (this.order_type === types.ORDER_TYPE_DELIVERY) {
        /*
         * ⚠️ place 與 end_location 的初始值都是 null（見 data 的 delivery:）。
         *    只有 created() 從 KEY_ROUTES 還原成功時才有值 —— 而那份快取有
         *    TTL，也可能因為 Google 的 geocode 從未成功而根本沒被寫入。
         *
         * 2026-08-25 之前這裡直接讀 .formatted_address / .lat，缺漏時拋
         * TypeError。它發生在 click handler 裡，而本專案沒有設
         * Vue.config.errorHandler，所以例外被完全吞掉：DOM 零變化、
         * 零網路請求、後端零日誌 —— 客人看到的是「結帳鈕按了沒反應」，
         * 重試也無效（VLocation 不會重算已存在的 order_type）。
         *
         * ⚠️ 最容易一次影響**所有**外送客人的情境，是 Google Maps 金鑰的
         *    HTTP referrer 白名單漏掉新網域：那時 geocode 的回呼完全不觸發，
         *    連 status !== 'OK' 的錯誤分支都跑不到，於是路線快取永遠寫不進去。
         *    換主機或換網域時務必先確認白名單 —— 見 ROADMAP 的切換清單。
         */
        if (
          this.delivery === null ||
          this.delivery.place === null ||
          this.delivery.end_location === null
        ) {
          console.error('[checkout] 外送路線資料缺漏，無法組出 map payload', {
            delivery: this.delivery !== null,
            place: this.delivery !== null && this.delivery.place !== null,
            end_location: this.delivery !== null && this.delivery.end_location !== null
          })

          this.show_dialog(this.$t('err'), this.$t('err_delivery_route'), 'delivery_reset')

          return
        }

        // 外送不帶 discountId（維持 null），折抵金額直接寫在 discount
        payload.total_price -= this.discount
        payload.discount = this.discount
        payload.map = {
          address: this.delivery.location,
          formatted_address: this.delivery.place.formatted_address,
          location: {
            lat: this.delivery.end_location.lat,
            lng: this.delivery.end_location.lng
          }
        }
      } else if (this.use_discount !== null) {
        payload.total_price -= this.discount
        payload.discountId = this.use_discount
        payload.discount = this.discount
      }

      this.setUserName(payload.name)
      this.setUserNumber(payload.number)
      this.setRemark(this.unified_compilation)
      this.setDiscount(this.use_discount)
      this.apiGetStoreStatus(payload)
    }
  },

  created() {
    // 整包取代 delivery（含 start_location / center / fee / distance / time / place）
    const routes = getStorage(KEY_ROUTES)
    if (routes !== null) {
      this.delivery = null
      this.delivery = routes
    }

    /*
     * LINE Pay 取消付款會導回 /checkout?q=<LINE user id>。
     * 比照 Home.vue:309 的作法讀取，讓 client_id 在 localStorage
     * 過期（1 小時）時仍能重新建立 —— 否則折價券與訂單通知都會失效。
     */
    const clientId = new URL(location.href).searchParams.get('q')
    if (clientId !== null) this.setClientID(clientId)
  },

  destroyed() {
    window.removeEventListener('pageshow', this.on_page_show)
  },

  mounted() {
    window.scrollTo(0, 0)

    /*
     * 從金流頁按「上一頁」回來時，把載入遮罩解掉。
     *
     * apiPostCart 送單成功後刻意不關閉 loading_show，讓遮罩撐到頁面跳走
     * （見該函式上方註解）。但瀏覽器的上一頁快取（bfcache）會把整個頁面
     * 狀態原封不動還原 —— 包含 loading_show = true 與 lockScroll()，
     * 而且不會重新執行 created / mounted，於是遮罩永遠轉不停、畫面也捲不動。
     *
     * 只在 event.persisted 為 true 時處理：那正是「由 bfcache 還原」的訊號。
     * 一般載入時 pageshow 也會觸發，但那時 mounted 可能已經啟動了合法的
     * 載入流程，無條件關閉會把正在進行的遮罩誤關掉。
     */
    window.addEventListener('pageshow', this.on_page_show)

    if (this.cart_list.length > 0) {
      // 兩支 API 並行、皆不 await，與原始碼一致
      this.apiGetUserDiscount()
      this.apiGetStoreStatus(null)
    }
  }
}
</script>
